<script setup>
import { ref, reactive } from 'vue';
import { useUserStore } from '@/stores/user.store.js';
import Helpers from '@/helpers/index.js';
import { API } from '@/services/index';
import { UPDATE_USER_PROFILE } from '@/services/user/mutations/profile/index.js';
import { useMutation } from '@tanstack/vue-query';
import { useFileUpload } from '@/composables/useFileUpload.js';
import HeaderPartial from '@/pages/partials/settings/headerPartial.vue';
import SectionPartial from '@/pages/partials/settings/sectionPartial.vue';
import { usePermissionsStore } from '@/stores/permissions.store.js';

const formRules = {
  email: (value) => !!value || 'Email is required.',
  emailRegex: (value) => /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,10}$/.test(value) || 'Email must be valid',
  phoneRegex: (value) => (value?.length > 9 && /[0-9-]+/.test(value)) || 'Phone number needs to be at least 10 digits.'
};

const user = useUserStore();
const permissions = usePermissionsStore();
const avatarUploadInput = ref();
const avatarFile = ref(null);
const username = ref(user.currentUser.username);
const first_name = ref(user.currentUser.first_name);
const last_name = ref(user.currentUser.last_name);
const email = ref(user.currentUser.email);
const phone = ref(user.currentUser.phone);

const isNameFormValid = ref(false);
const isEmailFormValid = ref(false);
const isEditPhoneValid = ref(false);
const isEditPhone = ref(false);
const isEditName = ref(false);
const isEditEmail = ref(false);
const updateForm = reactive({
  fieldType: null,
  isLoading: false,
  avatarRemoving: false
});

const userProfileMutation = useMutation({
  mutationFn: async (payload) => {
    return await API.sendQuery('/graphql/user', UPDATE_USER_PROFILE, payload);
  },
  onSuccess: (data) => {
    if (data.data.data.updateProfile) {
      user.updateCurrentUser(data.data.data.updateProfile);

      switch (updateForm.fieldType) {
        case 'name':
          isEditName.value = false;
          break;
        case 'email':
          isEditEmail.value = false;
          break;
        case 'phone':
          isEditPhone.value = false;
          break;
      }
    }

    if (data.data.error) {
      // Helpers.showError(data.data.error.message);
      console.log(data.data.error);
    }
    updateForm.isLoading = false;
    updateForm.avatarRemoving = false;
  },
  onError: (error) => {
    console.log('error', error);
    updateForm.fieldType = null;
    updateForm.isLoading = false;
    updateForm.avatarRemoving = false;
  }
});

const removeAvatar = async () => {
  updateForm.fieldType = 'avatar';
  updateForm.isLoading = true;
  userProfileMutation.mutateAsync({ avatar: '' });
};

const updateAvatarHandler = async (payload) => {
  updateForm.fieldType = 'avatar';
  updateForm.isLoading = true;

  if (payload?.removed) {
    updateForm.avatarRemoving = true;
    await userProfileMutation.mutateAsync({
      remove_avatar: true
    });
  } else {
    const { uploadFile, uploadErrors, uploadSuccess, uploadedFileLocation } = useFileUpload();

    if (avatarFile.value) {
      await uploadFile(avatarFile.value);
      if (uploadSuccess.value && uploadedFileLocation.value) {
        await userProfileMutation.mutateAsync({
          avatar: uploadedFileLocation.value
        });
      }
    }
  }
};

const updateNameHandler = async () => {
  if (isNameFormValid.value) {
    updateForm.fieldType = 'name';
    updateForm.isLoading = true;

    const payload = {
      first_name: first_name.value,
      last_name: last_name.value
    };

    await userProfileMutation.mutateAsync(payload);
  }
};

const updateEmailHandler = async () => {
  updateForm.fieldType = 'email';
  updateForm.isLoading = true;

  const payload = {
    email: email.value
  };

  await userProfileMutation.mutateAsync(payload);
};

const updatePhoneHandler = async () => {
  if (isEditPhoneValid.value) {
    updateForm.fieldType = 'phone';
    updateForm.isLoading = true;

    const payload = {
      phone: String(phone.value)
    };

    await userProfileMutation.mutateAsync(payload);
  }
};
</script>

<template>
  <div class="tw-max-w-full lg:tw-max-w-[750px]">
    <HeaderPartial title="My Profile" />
    <SectionPartial
      label="Profile Photo"
      subtitle="This helps your teammates recognize you in PartnrUp."
      :isLoading="updateForm.fieldType === 'avatar' && updateForm.isLoading"
    >
      <div class="tw-flex tw-flex-col tw-items-center tw-justify-between tw-pb-5 lg:tw-flex-row">
        <div class="tw-flex tw-items-center">
          <div>
            <v-avatar size="135" v-if="user.currentUser?.avatar">
              <v-img contain width="135" :src="user.currentUser.avatar" :alt="user.currentUser.first_name"></v-img>
            </v-avatar>
            <v-avatar color="primary-blue" size="80" v-else>
              <span class="tw-h-20 tw-w-20 tw-text-[1.688rem] tw-leading-[80px]">
                {{ Helpers.getUserInitials(user.currentUser) }}
              </span>
            </v-avatar>
          </div>
        </div>
        <div
          class="tw-mt-4 tw-flex tw-w-full tw-space-x-2 lg:tw-mt-0 lg:tw-w-[16rem] lg:tw-items-center"
          :class="user.currentUser?.avatar ? 'tw-justify-between' : 'tw-justify-end'"
        >
          <v-btn
            class="tw-capitalize"
            variant="tonal"
            size="small"
            height="40px"
            @click="avatarUploadInput.click()"
            :disabled="updateForm.isLoading"
            :loading="updateForm.isLoading && !updateForm.avatarRemoving"
          >
            {{ user.currentUser?.avatar ? 'Change Photo' : 'Upload Photo' }}
          </v-btn>
          <v-btn
            v-if="user.currentUser.avatar"
            class="tw-capitalize"
            variant="tonal"
            size="small"
            height="40px"
            @click="updateAvatarHandler({ removed: true })"
            :disabled="updateForm.isLoading"
            :loading="updateForm.avatarRemoving"
            color="red"
          >
            Remove Photo
          </v-btn>
        </div>
        <v-file-input
          v-model="avatarFile"
          ref="avatarUploadInput"
          accept="image/*"
          class="tw-hidden"
          @update:modelValue="updateAvatarHandler"
        ></v-file-input>
      </div>
    </SectionPartial>
    <SectionPartial :label="'Name'" :isLoading="updateForm.fieldType === 'name' && updateForm.isLoading">
      <template v-if="!isEditName">
        <div class="tw-flex tw-items-center tw-justify-between">
          <p class="tw-mb-0 tw-text-sm tw-text-[#0d1216]">
            {{ `${first_name ?? ''} ${last_name ?? ''}` }}
          </p>
          <v-btn class="tw-rounded-sm tw-capitalize" @click="isEditName = true" variant="tonal" size="small" height="40px"> Edit </v-btn>
        </div>
      </template>
      <template v-else>
        <v-form ref="nameForm" v-model="isNameFormValid" @submit.prevent="updateNameHandler">
          <div class="tw-flex tw-flex-col tw-items-start tw-justify-between tw-space-y-2 lg:tw-flex-row lg:tw-space-x-2 lg:tw-space-y-0">
            <div class="tw-flex tw-w-full tw-flex-col">
              <v-text-field
                v-model="first_name"
                variant="outlined"
                color="primary-blue"
                label=""
                placeholder="First name"
                hide-details="auto"
                density="compact"
                :disabled="updateForm.isLoading && updateForm.fieldType === 'name'"
              ></v-text-field>
            </div>
            <div class="tw-flex tw-w-full tw-flex-col">
              <v-text-field
                v-model="last_name"
                variant="outlined"
                color="primary-blue"
                label=""
                placeholder="Last name"
                hide-details="auto"
                density="compact"
                :disabled="updateForm.isLoading && updateForm.fieldType === 'name'"
              ></v-text-field>
            </div>
            <div class="tw-mt-3 tw-flex tw-w-full tw-items-center tw-justify-between lg:tw-ml-16 lg:tw-mt-0 lg:tw-w-[9rem] lg:tw-space-x-3">
              <v-btn
                class="tw-capitalize"
                variant="tonal"
                size="small"
                height="40px"
                @click="isEditName = false"
                :disabled="updateForm.isLoading && updateForm.fieldType === 'name'"
              >
                Cancel
              </v-btn>
              <v-btn
                class="tw-capitalize"
                color="primary-blue"
                variant="tonal"
                size="small"
                height="40px"
                type="submit"
                :loading="updateForm.isLoading && updateForm.fieldType === 'name'"
                :disabled="updateForm.isLoading && updateForm.fieldType === 'name'"
              >
                Save
              </v-btn>
            </div>
          </div>
        </v-form>
      </template>
    </SectionPartial>
    <!--    <sectionPartial :label="'Email'" :isLoading="updateForm.fieldType === 'email' && updateForm.isLoading">-->
    <!--      <template v-if="!isEditEmail">-->
    <!--        <div class="tw-flex tw-items-start tw-justify-between">-->
    <!--          <p class="tw-mb-0 tw-text-sm tw-text-[#0d1216]">-->
    <!--            {{ email }}-->
    <!--          </p>-->
    <!--          <v-btn class="tw-rounded-sm tw-capitalize" @click="isEditEmail = true" variant="tonal" height="40px" size="small"> Edit </v-btn>-->
    <!--        </div>-->
    <!--      </template>-->
    <!--      <template v-else>-->
    <!--        <v-form ref="emailForm" v-model="isEmailFormValid" @submit.prevent="updateEmailHandler">-->
    <!--          <div class="tw-flex tw-items-start tw-justify-between tw-space-x-2">-->
    <!--            <div class="tw-flex tw-w-full tw-flex-col">-->
    <!--              <v-text-field-->
    <!--                v-model="email"-->
    <!--                :rules="[formRules.email, formRules.emailRegex]"-->
    <!--                variant="outlined"-->
    <!--                color="secondary-blue"-->
    <!--                label=""-->
    <!--                placeholder="Email*"-->
    <!--                hide-details="auto"-->
    <!--                density="compact"-->
    <!--                :focused="true"-->
    <!--                :disabled="updateForm.isLoading && updateForm.fieldType === 'email'"-->
    <!--              ></v-text-field>-->
    <!--            </div>-->
    <!--            <div class="tw-ml-16 tw-flex tw-items-center tw-justify-between tw-space-x-3">-->
    <!--              <v-btn-->
    <!--                size="small"-->
    <!--                class="tw-rounded-sm tw-font-bold tw-capitalize"-->
    <!--                height="40px"-->
    <!--                variant="tonal"-->
    <!--                @click="isEditEmail = false"-->
    <!--                :disabled="updateForm.isLoading && updateForm.fieldType === 'email'"-->
    <!--              >-->
    <!--                Cancel-->
    <!--              </v-btn>-->
    <!--              <v-btn-->
    <!--                size="small"-->
    <!--                class="tw-rounded-sm tw-font-bold tw-capitalize tw-text-white"-->
    <!--                height="40px"-->
    <!--                color="secondary-blue"-->
    <!--                type="submit"-->
    <!--                :loading="updateForm.isLoading && updateForm.fieldType === 'email'"-->
    <!--                :disabled="updateForm.isLoading && updateForm.fieldType === 'email'"-->
    <!--              >-->
    <!--                Save-->
    <!--              </v-btn>-->
    <!--            </div>-->
    <!--          </div>-->
    <!--        </v-form>-->
    <!--      </template>-->
    <!--    </sectionPartial>-->
    <sectionPartial label="Phone" :divider="false" :isLoading="updateForm.fieldType === 'phone' && updateForm.isLoading">
      <template v-if="!isEditPhone">
        <div class="tw-flex tw-items-start tw-justify-between">
          <p class="tw-mb-0 tw-text-sm tw-text-[#0d1216]">
            {{ phone }}
          </p>
          <v-btn class="tw-rounded-sm tw-capitalize" @click="isEditPhone = true" variant="tonal" height="40px" size="small"> Edit </v-btn>
        </div>
      </template>
      <template v-else>
        <v-form ref="phoneForm" v-model="isEditPhoneValid" @submit.prevent="updatePhoneHandler">
          <div class="tw-flex tw-flex-col tw-items-start tw-justify-between tw-space-y-2 lg:tw-flex-row lg:tw-space-x-2 lg:tw-space-y-0">
            <div class="tw-flex tw-w-full tw-flex-col">
              <v-text-field
                v-model="phone"
                :rules="[formRules.phoneRegex]"
                variant="outlined"
                color="primary-blue"
                label=""
                placeholder="Phone number"
                hide-details="auto"
                density="compact"
                :disabled="updateForm.isLoading && updateForm.fieldType === 'phone'"
              ></v-text-field>
            </div>
            <div class="tw-mt-3 tw-flex tw-w-full tw-items-center tw-justify-between lg:tw-ml-16 lg:tw-mt-0 lg:tw-w-[9rem] lg:tw-space-x-3">
              <v-btn
                class="tw-capitalize"
                variant="tonal"
                size="small"
                height="40px"
                @click="isEditPhone = false"
                :disabled="updateForm.isLoading && updateForm.fieldType === 'phone'"
              >
                Cancel
              </v-btn>
              <v-btn
                class="tw-capitalize"
                color="primary-blue"
                variant="tonal"
                size="small"
                height="40px"
                type="submit"
                :loading="updateForm.isLoading && updateForm.fieldType === 'phone'"
              >
                Save
              </v-btn>
            </div>
          </div>
        </v-form>
      </template>
    </sectionPartial>
  </div>
</template>

<style scoped>
.filepond--drop-label {
  color: #4c4e53;
}

.filepond--label-action {
  text-decoration-color: #babdc0;
}

.filepond--panel-root {
  background-color: #edf0f4;
}

.filepond--root {
  width: 100px;
  margin: 0 auto;
}
</style>
